/**
 * ServiceCatalogPage — iter-272 (Feb 2026)
 * =========================================
 * Reusable admin page that renders the EXACT same "cards + add-modal +
 * details-with-required-documents" flow as VisaIdServices, but driven by
 * config props so a single component powers multiple catalog hubs
 * (Legal Translation, Tax Services, …).
 *
 * Props (all required):
 *   category            — path segment: 'legal_translation' | 'tax_services'
 *   titleAr / titleEn   — H1 shown to the operator
 *   subtitleAr / subtitleEn
 *   accentColor         — Tailwind color token, e.g. 'rose', 'emerald'
 *   backPath            — path the "Back" button navigates to
 *   breadcrumb          — [{labelAr, labelEn, path?}]  ordered leaf-first? no — root-first.
 *   testIdPrefix        — data-testid namespace
 *   detailPathBase      — for viewService: navigate(`${detailPathBase}/${service.id}`)
 */
import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FileText, Plus, Edit3, Trash2, Loader2, MapPin,
  Check, Eye, FileCheck, Settings, Languages, Receipt, Calculator, Stamp,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import DashboardLayout from '../components/DashboardLayout';
import EmirateDocsSection from '../components/services/EmirateDocsSection';
import ServiceFormModal from '../components/services/ServiceFormModal';
import EmiratePricingPanel from '../components/services/EmiratePricingPanel';
import { pricingSummary } from '../components/services/serviceEmirates';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ICONS = {
  FileText, Languages, Receipt, Calculator, Stamp, FileCheck,
};

/** Blank form template used on Add. Consumers may pass a `defaultServiceType`. */

export default function ServiceCatalogPage({
  category,
  titleAr, titleEn,
  subtitleAr, subtitleEn,
  accentColor = 'rose',
  backPath,
  breadcrumb,
  testIdPrefix = 'catalog',
  detailPathBase,
}) {
  const navigate = useNavigate();
  const { serviceId } = useParams();
  const { language } = useLanguage();
  const { token } = useAuth();
  const isAr = language === 'ar';

  const [meta, setMeta] = useState(null);           // { service_types: [...] }
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const [selectedService, setSelectedService] = useState(null);
  const [activeTab, setActiveTab] = useState('details');

  const [savingEmirates, setSavingEmirates] = useState(false);

  const headers = () => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' });

  // Fetch metadata (allowed service types + labels)
  const fetchMeta = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/service-catalog/meta/${category}`, { headers: headers() });
      setMeta(r.data);
    } catch (e) {
      toast.error(isAr ? 'فشل تحميل إعدادات الفئة' : 'Failed to load category meta');
    }
  };

  const fetchServices = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/service-catalog/${category}`, { headers: headers() });
      setServices(r.data?.data || []);
    } catch (e) {
      toast.error(isAr ? 'خطأ في جلب الخدمات' : 'Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMeta(); fetchServices(); /* eslint-disable-next-line */ }, [category]);

  // Bind service from URL params
  useEffect(() => {
    if (serviceId && services.length > 0) {
      const s = services.find((x) => x.id === serviceId);
      if (s) { setSelectedService(s); setActiveTab('details'); }
    }
  }, [serviceId, services]);

  const openAddModal = () => { setEditingService(null); setShowModal(true); };
  const openEditModal = (service) => { setEditingService(service); setShowModal(true); };

  // Simplified form (Jun 2026): main/sub name + descriptions + active. Pricing is per emirate.
  const handleSave = async (payload) => {
    setSaving(true);
    try {
      if (editingService) {
        await axios.put(`${API_URL}/api/service-catalog/${category}/${editingService.id}`, payload, { headers: headers() });
        toast.success(isAr ? 'تم تحديث الخدمة بنجاح' : 'Service updated successfully');
      } else {
        await axios.post(`${API_URL}/api/service-catalog/${category}`, payload, { headers: headers() });
        toast.success(isAr ? 'تم إضافة الخدمة بنجاح' : 'Service added successfully');
      }
      setShowModal(false);
      fetchServices();
    } catch (e) {
      toast.error(e.response?.data?.detail || (isAr ? 'خطأ في حفظ البيانات' : 'Error saving data'));
    } finally {
      setSaving(false);
    }
  };

  const saveEmirates = async (emirate_pricing) => {
    setSavingEmirates(true);
    try {
      await axios.put(`${API_URL}/api/service-catalog/${category}/${selectedService.id}`, { emirate_pricing }, { headers: headers() });
      const r = await axios.get(`${API_URL}/api/service-catalog/${category}`, { headers: headers() });
      const list = r.data?.data || [];
      setServices(list);
      const fresh = list.find((x) => x.id === selectedService.id);
      if (fresh) setSelectedService(fresh);
      toast.success(isAr ? 'تم حفظ أسعار الإمارات' : 'Emirate prices saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || (isAr ? 'خطأ في حفظ الأسعار' : 'Error saving prices'));
    } finally {
      setSavingEmirates(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(isAr ? 'هل أنت متأكد من الحذف؟' : 'Delete this service?')) return;
    setDeleting(id);
    try {
      await axios.delete(`${API_URL}/api/service-catalog/${category}/${id}`, { headers: headers() });
      toast.success(isAr ? 'تم الحذف' : 'Deleted');
      await fetchServices();
    } catch (e) {
      toast.error(isAr ? 'خطأ في الحذف' : 'Delete failed');
    } finally {
      setDeleting(null);
    }
  };

  const toggleActive = async (service) => {
    const next = !service.is_active;
    setServices((prev) => prev.map((s) => s.id === service.id ? { ...s, is_active: next } : s));
    try {
      await axios.put(`${API_URL}/api/service-catalog/${category}/${service.id}`, { is_active: next }, { headers: headers() });
      toast.success(next ? (isAr ? 'تم التفعيل' : 'Activated') : (isAr ? 'تم التعطيل' : 'Deactivated'));
    } catch {
      setServices((prev) => prev.map((s) => s.id === service.id ? { ...s, is_active: !next } : s));
      toast.error(isAr ? 'فشل التحديث' : 'Update failed');
    }
  };

  const viewService = (service) => {
    setSelectedService(service);
    setActiveTab('details');
    if (detailPathBase) navigate(`${detailPathBase}/${service.id}`);
  };

  // Accent-color Tailwind classes — we can't string-interpolate Tailwind
  // safely, so keep a small allowlist.
  const ACCENT = useMemo(() => {
    const map = {
      rose:    { text: 'text-rose-400',    bg: 'bg-rose-500/20',    bgLite: 'bg-rose-500/10',    border: 'border-rose-500/30', focus: 'focus:border-rose-400', hover: 'hover:bg-rose-500/20', btn: 'bg-rose-600 hover:bg-rose-700', accentInp: 'accent-rose-500' },
      emerald: { text: 'text-emerald-400', bg: 'bg-emerald-500/20', bgLite: 'bg-emerald-500/10', border: 'border-emerald-500/30', focus: 'focus:border-emerald-400', hover: 'hover:bg-emerald-500/20', btn: 'bg-emerald-600 hover:bg-emerald-700', accentInp: 'accent-emerald-500' },
      amber:   { text: 'text-amber-400',   bg: 'bg-amber-500/20',   bgLite: 'bg-amber-500/10',   border: 'border-amber-500/30', focus: 'focus:border-amber-400', hover: 'hover:bg-amber-500/20', btn: 'bg-amber-600 hover:bg-amber-700', accentInp: 'accent-amber-500' },
      cyan:    { text: 'text-cyan-400',    bg: 'bg-cyan-500/20',    bgLite: 'bg-cyan-500/10',    border: 'border-cyan-500/30', focus: 'focus:border-cyan-400', hover: 'hover:bg-cyan-500/20', btn: 'bg-cyan-600 hover:bg-cyan-700', accentInp: 'accent-cyan-500' },
    };
    return map[accentColor] || map.rose;
  }, [accentColor]);


  // ─── Detail View ────────────────────────────────────────────────────
  if (selectedService) {
    const IconComponent = ICONS[selectedService.icon] || FileText;

    return (
      <DashboardLayout
        title={isAr ? selectedService.name_ar : selectedService.name_en}
        titleEn={selectedService.name_en}
        subtitle={isAr ? 'إدارة بيانات الخدمة والأوراق المطلوبة' : 'Manage service details and required documents'}
        subtitleEn="Manage service details and required documents"
        backPath={detailPathBase}
        onBack={() => { setSelectedService(null); navigate(detailPathBase); }}
        breadcrumb={[
          ...breadcrumb,
          { labelAr: selectedService.name_ar, labelEn: selectedService.name_en },
        ]}
      >
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6" data-testid={`${testIdPrefix}-detail`}>
          {/* Identity strip */}
          <div className="flex items-center gap-4 bg-[#1a1a2e] border border-[#2a2a4e] rounded-2xl p-5">
            <div className={`w-14 h-14 rounded-xl ${ACCENT.bg} border ${ACCENT.border} flex items-center justify-center shrink-0`}>
              <IconComponent className={`h-7 w-7 ${ACCENT.text}`} />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-xl font-bold text-white truncate">
                {isAr ? selectedService.name_ar : selectedService.name_en}
              </h1>
              <div className="flex items-center gap-4 text-sm text-[#A1A1AA] mt-1">
                <span className="flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  {pricingSummary(selectedService, isAr).count}/7 {isAr ? 'إمارات' : 'emirates'} · {pricingSummary(selectedService, isAr).text}
                </span>
                <span className={`flex items-center gap-1 ${selectedService.is_active ? 'text-emerald-300' : 'text-red-300'}`}>
                  <Check className="h-4 w-4" />
                  {selectedService.is_active ? (isAr ? 'مفعّل' : 'Active') : (isAr ? 'معطّل' : 'Inactive')}
                </span>
              </div>
            </div>
            <Button onClick={() => openEditModal(selectedService)} variant="outline"
                    className="border-[#2a2a4e] text-white shrink-0"
                    data-testid={`${testIdPrefix}-edit-service-btn`}>
              <Edit3 className="h-4 w-4 me-2" />
              {isAr ? 'تعديل' : 'Edit'}
            </Button>
          </div>

          {/* Tabs */}
          <div className="flex gap-2">
            <button
              onClick={() => setActiveTab('details')}
              className={`px-4 py-2 rounded-lg transition-colors ${activeTab === 'details' ? 'bg-[#c9a227] text-black' : 'bg-[#1a1a2e] text-white hover:bg-[#2a2a4e]'}`}
              data-testid={`${testIdPrefix}-tab-details`}
            >
              <Eye className="h-4 w-4 inline me-2" />
              {isAr ? 'تفاصيل الخدمة' : 'Service Details'}
            </button>
            <button
              onClick={() => setActiveTab('documents')}
              className={`px-4 py-2 rounded-lg transition-colors ${activeTab === 'documents' ? 'bg-[#c9a227] text-black' : 'bg-[#1a1a2e] text-white hover:bg-[#2a2a4e]'}`}
              data-testid={`${testIdPrefix}-tab-documents`}
            >
              <FileCheck className="h-4 w-4 inline me-2" />
              {isAr ? 'الأوراق المطلوبة' : 'Required Documents'}
            </button>
          </div>

          {activeTab === 'details' && (
            <div className="bg-[#1a1a2e] rounded-2xl border border-[#2a2a4e] p-6 space-y-4">
              {/* iter-274 — display Main + Sub service names when linked. */}
              {(selectedService.main_name_ar || selectedService.main_service_id) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-3 rounded-lg border border-cyan-500/20 bg-cyan-500/[0.03]">
                  <Info label={isAr ? 'اسم الخدمة الرئيسية' : 'Main Service Name'}
                        value={isAr ? (selectedService.main_name_ar || '-') : (selectedService.main_name_en || selectedService.main_name_ar || '-')} />
                  <Info label={isAr ? 'اسم الخدمة الفرعية' : 'Sub Service Name'}
                        value={isAr ? (selectedService.name_ar || '-') : (selectedService.name_en || selectedService.name_ar || '-')} />
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Info label={isAr ? 'الاسم بالعربية' : 'Arabic Name'} value={selectedService.name_ar} />
                <Info label={isAr ? 'الاسم بالإنجليزية' : 'English Name'} value={selectedService.name_en} />
              </div>
              <Info label={isAr ? 'الوصف بالعربية' : 'Arabic Description'} value={selectedService.description_ar || '-'} />
              <Info label={isAr ? 'الوصف بالإنجليزية' : 'English Description'} value={selectedService.description_en || '-'} />
              <EmiratePricingPanel
                service={selectedService}
                onSave={saveEmirates}
                saving={savingEmirates}
                language={language}
                accentTw={{ icon: ACCENT.text, border: ACCENT.border, switchOn: '' }}
                testIdPrefix={testIdPrefix}
              />
            </div>
          )}

          {activeTab === 'documents' && (
            <EmirateDocsSection
              service={selectedService}
              language={language}
              testIdPrefix={testIdPrefix}
            />
          )}
        </section>
        <ServiceFormModal
          open={showModal}
          onOpenChange={setShowModal}
          service={editingService}
          onSubmit={handleSave}
          saving={saving}
          language={language}
          PageIcon={FileText}
          accentIcon={ACCENT.text}
          testIdPrefix={testIdPrefix}
        />
      </DashboardLayout>
    );
  }

  // ─── List View ──────────────────────────────────────────────────────
  return (
    <DashboardLayout
      title={titleAr}
      titleEn={titleEn}
      subtitle={subtitleAr}
      subtitleEn={subtitleEn}
      backPath={backPath}
      breadcrumb={breadcrumb}
    >
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid={`${testIdPrefix}-list`}>
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-xl ${ACCENT.bgLite} border ${ACCENT.border} flex items-center justify-center`}>
              <FileText className={`h-6 w-6 ${ACCENT.text}`} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">{isAr ? 'الخدمات المتاحة' : 'Available Services'}</h2>
              <p className="text-sm text-[#71717A]">{services.length} {isAr ? 'خدمة' : 'services'}</p>
            </div>
          </div>
          <Button onClick={openAddModal} className="bg-[#d4af37] hover:bg-[#c9a227] text-black"
                  data-testid={`${testIdPrefix}-add-service-btn`}>
            <Plus className="h-4 w-4 me-2" />
            {isAr ? 'إضافة خدمة جديدة' : 'Add New Service'}
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className={`h-8 w-8 animate-spin ${ACCENT.text}`} />
          </div>
        ) : services.length === 0 ? (
          <div className="text-center py-20 bg-[#1a1a2e] rounded-2xl border border-[#2a2a4e]">
            <FileText className={`h-16 w-16 mx-auto mb-4 ${ACCENT.text} opacity-30`} />
            <p className="text-lg text-[#808080]">{isAr ? 'لا توجد خدمات بعد' : 'No services yet'}</p>
            <p className="text-sm text-[#606060] mt-2">{isAr ? 'اضغط "إضافة خدمة جديدة" للبدء' : 'Click "Add New Service" to start'}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => {
              const IconComponent = ICONS[service.icon] || FileText;
              return (
                <div
                  key={service.id}
                  className={`bg-[#1a1a2e] rounded-2xl border border-[#2a2a4e] overflow-hidden hover:border-${accentColor}-500/50 transition-all group flex flex-col ${!service.is_active ? 'opacity-70' : ''}`}
                  data-testid={`${testIdPrefix}-card-${service.id}`}
                >
                  <div className="flex items-center justify-between px-6 pt-4 pb-2 border-b border-white/5">
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={!!service.is_active}
                        onCheckedChange={() => toggleActive(service)}
                        className="data-[state=checked]:bg-emerald-500"
                        data-testid={`${testIdPrefix}-active-${service.id}`}
                      />
                      <span className={`text-[11px] font-medium ${service.is_active ? 'text-emerald-300' : 'text-[#71717A]'}`}>
                        {service.is_active ? (isAr ? 'مفعّلة' : 'Active') : (isAr ? 'موقوفة' : 'Paused')}
                      </span>
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => openEditModal(service)} className="p-2 rounded-lg bg-[#0a0a0a] hover:bg-[#2a2a4e]"
                              data-testid={`${testIdPrefix}-edit-${service.id}`}>
                        <Edit3 className="h-4 w-4 text-blue-400" />
                      </button>
                      <button onClick={() => handleDelete(service.id)} disabled={deleting === service.id}
                              className="p-2 rounded-lg bg-[#0a0a0a] hover:bg-red-500/20"
                              data-testid={`${testIdPrefix}-delete-${service.id}`}>
                        {deleting === service.id ? <Loader2 className="h-4 w-4 animate-spin text-red-400" /> : <Trash2 className="h-4 w-4 text-red-400" />}
                      </button>
                    </div>
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <div className={`w-14 h-14 rounded-xl ${ACCENT.bg} flex items-center justify-center group-hover:scale-110 transition-transform mb-4`}>
                      <IconComponent className={`h-7 w-7 ${ACCENT.text}`} />
                    </div>
                    <h3 className="text-lg font-bold text-white mb-2">{isAr ? service.name_ar : service.name_en}</h3>
                    <p className="text-[#808080] text-sm mb-4 line-clamp-2">{isAr ? service.description_ar : service.description_en}</p>
                    <div className="flex items-center justify-between text-sm mt-auto">
                      <span className="text-[#c9a227] font-bold" data-testid={`${testIdPrefix}-card-total-${service.id}`}>
                        {pricingSummary(service, isAr).text}
                      </span>
                      <span className="text-[#808080] flex items-center gap-1" data-testid={`${testIdPrefix}-card-emirates-${service.id}`}>
                        <MapPin className="h-4 w-4" />
                        {pricingSummary(service, isAr).count}/7 {isAr ? 'إمارات' : 'emirates'}
                      </span>
                    </div>
                  </div>
                  <button onClick={() => viewService(service)}
                          className={`w-full p-3 ${ACCENT.bgLite} ${ACCENT.text} text-sm font-medium ${ACCENT.hover} transition-colors flex items-center justify-center gap-2 border-t border-white/5`}
                          data-testid={`${testIdPrefix}-manage-${service.id}`}>
                    <Settings className="h-4 w-4" />
                    {isAr ? 'إدارة الخدمة' : 'Manage Service'}
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <ServiceFormModal
          open={showModal}
          onOpenChange={setShowModal}
          service={editingService}
          onSubmit={handleSave}
          saving={saving}
          language={language}
          PageIcon={FileText}
          accentIcon={ACCENT.text}
          testIdPrefix={testIdPrefix}
        />
      </section>
    </DashboardLayout>
  );
}

// ─── Sub-components ────────────────────────────────────────────────────
function Info({ label, value }) {
  return (
    <div className="bg-[#0a0a0a] rounded-xl p-4">
      <p className="text-[#808080] text-sm mb-1">{label}</p>
      <p className="text-white font-medium whitespace-pre-wrap">{value}</p>
    </div>
  );
}
